import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { setSkipIntro } from '~extension/web2print/controller/api/duplicateForm';
import { queries } from '~extension/web2print/controller/api/Queries';
import { type FormSettings } from '~extension/web2print/model/FormSettings';
import { DuplicateForm } from '~extension/web2print/view/DuplicateForm/DuplicateForm';
import { DuplicateIntro } from '~extension/web2print/view/DuplicateForm/DuplicateIntro';
import { Message } from '~extension/web2print/view/MessageModal/Message';
import { type AssetId } from '~modules/media-library/model/Asset';

type DuplicateDetailControllerProps = {
  assetId: AssetId;
  onClose?: () => void;
};

export function DuplicateDetailController({ assetId, onClose }: DuplicateDetailControllerProps) {
  const [showIntro, setShowIntro] = useState<boolean>(undefined);
  const [formSettings, setFormSettings] = useState<FormSettings>(undefined);

  useEffect(() => {
    setShowIntro(undefined);
    setFormSettings(undefined);
  }, [assetId]);

  const useSetSkipIntro = () => {
    return useMutation({
      mutationFn: async () => Boolean(await setSkipIntro()),
    });
  };
  const { mutate } = useSetSkipIntro();

  // load form settings
  const { data: formSettingsData } = useQuery({
    ...queries.web2print.duplicateFormSettings(assetId),
    enabled: Boolean(assetId),
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (formSettingsData === undefined) return;
    setFormSettings(formSettingsData);
    setShowIntro(formSettingsData.showIntro);
  }, [formSettingsData]);

  const onStart = useCallback(
    (skipIntro: boolean) => {
      if (skipIntro) mutate();

      setShowIntro(false);
    },
    [mutate],
  );

  if (!Boolean(formSettings)) return null;

  if (Boolean(formSettings.message)) {
    return <Message message={formSettings.message} />;
  } else if (showIntro) return <DuplicateIntro onStart={onStart} />;
  else return <DuplicateForm formSettings={formSettings} onClose={onClose} />;
}
